import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "hooks/useAuth";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import { PageContainer, PageHeader } from "components/page";
import Card from "components/card";
import Badge from "components/badge";
import { Select } from "components/fields";
import ProfileDetails from "./profile-details";
import SecurityForm from "./security-form";
import NotificationSettings from "./notifications";
import GeneralSettings from "./general-settings";
import StaffSettings from "./staff-settings";
import CampusManagement from "./campus-management";
import IntegrationSettings from "./integrations";
import ApiTokenSettings from "./api-tokens";

const TOKEN_ADMIN_ROLES = ["admin", "sub_admin", "super_admin"];

const SETTINGS_NAV = [
  {
    group: "Account",
    items: [
      { key: "profile", label: "Profile" },
      { key: "security", label: "Security" },
      { key: "notifications", label: "Notifications" },
    ],
  },
  {
    group: "Organization",
    items: [
      { key: "general", label: "General" },
      { key: "staff", label: "User Management" },
      { key: "campuses", label: "Campuses" },
    ],
  },
  {
    group: "Integrations",
    items: [
      { key: "integrations", label: "Connected Services" },
      { key: "api-tokens", label: "API Tokens" },
    ],
  },
];

const PATHWAY_ALLOWED_GROUPS = ["Account"];

const SettingsNav = ({ groups, activeTab, onSelect }) => (
  <>
    <Select
      className="w-full md:hidden"
      aria-label="Settings section"
      value={activeTab}
      onChange={(e) => onSelect(e.target.value)}
    >
      {groups.map(({ group, items, locked }) => (
        <optgroup key={group} label={locked ? `${group} (Upgrade)` : group}>
          {items.map(({ key, label }) => (
            <option key={key} value={key} disabled={locked}>
              {label}
            </option>
          ))}
        </optgroup>
      ))}
    </Select>

    <Card as="nav" aria-label="Settings" className="hidden p-3 md:flex">
      {groups.map(({ group, items, locked }, idx) => (
        <div key={group} className={idx > 0 ? "mt-3 border-t border-line pt-3" : ""}>
          <p className="mb-1 flex items-center gap-2 px-3 text-xs font-semibold uppercase tracking-wider text-fg-muted">
            {group}
            {locked && (
              <Badge size="sm" tone="brand" className="normal-case tracking-normal">
                Upgrade
              </Badge>
            )}
          </p>
          <ul>
            {items.map(({ key, label }) => {
              const active = activeTab === key;
              return (
                <li key={key}>
                  <button
                    type="button"
                    onClick={() => onSelect(key)}
                    disabled={locked}
                    aria-current={active ? "page" : undefined}
                    className={`focus-ring w-full rounded-control px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                      active
                        ? "bg-surface-subtle font-semibold text-fg"
                        : "text-fg-muted hover:bg-surface-subtle hover:text-fg"
                    }`}
                  >
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </Card>
  </>
);

const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "profile";
  const { user } = useAuth();
  const isPathway = user?.customerStatus === "pathway";
  const canManageTokens = TOKEN_ADMIN_ROLES.includes(user?.role);

  // The API token tab is admin-only (the backend enforces it as well).
  const navGroups = SETTINGS_NAV.map((group) => ({
    ...group,
    locked: isPathway && !PATHWAY_ALLOWED_GROUPS.includes(group.group),
    items: canManageTokens ? group.items : group.items.filter((i) => i.key !== "api-tokens"),
  }));

  useEffect(() => {
    track(EVENTS.SETTINGS_PAGE_VIEWED);
  }, []);

  const handleTabChange = (tabName) => {
    setSearchParams({ tab: tabName });
  };

  return (
    <PageContainer
      variant="split"
      header={
        <PageHeader
          title="Settings"
          description="Manage your account, organization and connected services."
        />
      }
      aside={<SettingsNav groups={navGroups} activeTab={tab} onSelect={handleTabChange} />}
    >
      {tab === "profile" && <ProfileDetails />}
      {tab === "security" && <SecurityForm user={user} />}
      {tab === "notifications" && <NotificationSettings />}
      {tab === "general" && <GeneralSettings />}
      {tab === "staff" && <StaffSettings />}
      {tab === "campuses" && <CampusManagement />}
      {tab === "integrations" && <IntegrationSettings />}
      {tab === "api-tokens" && canManageTokens && <ApiTokenSettings />}
    </PageContainer>
  );
};

export default Settings;
